import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import { useEventListener } from '@vueuse/core'
import { convertBlobUrlsToBase64 } from '@/mixins/blob-utils'
import { useAuth } from '@/composables/useAuth'
import { useEprintFunnel } from '@/composables/format-edit/useEprintFunnel'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type { EprintSession } from '@/composables/format-edit/useEprintSession'
import { isCashlessSupported } from '@/utils/format-edit/eprintOptions'
import { layoutFromFormatId } from '@/utils/format-edit/eprintPrintFlag'
import templateConfigsJson from '@/utils/format-edit/eprintTemplateConfigs.json'
import type { EprintTemplateConfig } from '@/utils/format-edit/eprintOptions'
import { emitter } from '@/plugins/emitter'

const templateConfigs = templateConfigsJson as Record<
  string,
  EprintTemplateConfig
>

type OpenHtml = { $el?: { innerHTML: string }; innerHTML?: string }

export function useEprintOpen(
  session: EprintSession,
  deps: {
    cleanupMap: () => void
    stopPaypayRecheck: () => void
    searchNearCurrentLocation: () => void
    completePaypayEprint: (merchantPaymentId: string) => void
  }
) {
  const { isAuthenticated } = useAuth()
  const { globalVar, cookies } = useHostServices()
  const funnel = useEprintFunnel()

  function scrollToTop() {
    nextTick(() => {
      let el: HTMLElement | null = session.dialogRootEl
      while (el && el !== document.body) {
        if (el.scrollHeight > el.clientHeight + 1) {
          if (typeof el.scrollTo === 'function') {
            el.scrollTo({ top: 0, behavior: 'smooth' })
          } else {
            el.scrollTop = 0
          }
        }
        el = el.parentElement
      }
      if (typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    })
  }

  async function openDialog(
    id: string,
    html: OpenHtml,
    selFontStyle4DB?: string,
    resumeAddress?: string,
    isMypage = false
  ) {
    session.isMypage = isMypage
    session.resumeAddress = resumeAddress || ''
    if (session.eprintResultCode === '100') {
      session.isPrePayment = false
      session.selectedStore = null
    }
    if (session.eprintProcessingPending) {
      session.eprintProcessingPending = false
      session.isSevenElevenButtonDisabled = false
      session.paymentSelectorApi?.resetDisabled()
    }
    let formatId = id
    if (html.$el) {
      session.previewHtml = html.$el.innerHTML
    } else {
      session.previewHtml = html.innerHTML || ''
      formatId = 'shokumukeirekisho'
    }
    session.previewHtml = await convertBlobUrlsToBase64(session.previewHtml)
    session.formatid = formatId
    session.selFontStyle4DB = selFontStyle4DB || ''
    const layout = layoutFromFormatId(formatId)
    session.paperInfo = layout.paperInfo
    session.paperNumb = layout.paperNumb
    session.paperNumb2 = layout.paperNumb2
    session.printType = layout.printType
    session.printFlag = layout.printFlag
    if (templateConfigs[session.printFlag]) {
      session.printType = templateConfigs[session.printFlag].recommended
    }
    session.visible = true
    if (formatId === 'letter' || formatId === 'taishokutodoke') {
      session.isNotLetter = false
      session.printType = '13'
      session.printFlag = 'letter'
    }
    const nowdate = new Date()
    const date1 = new Date('2023/06/06 01:00:00')
    const date2 = new Date('2023/06/06 02:00:00')
    if (nowdate > date1 && nowdate < date2) {
      session.eprintResultCode = 'maintenance'
    }
    session.funnelSessionId = funnel.generateFunnelSessionId()
    session.dialogOpenedAt = new Date()
    session.previousClickedPrintType = null
    session.dialogLayoutReady = false
    const templateConfig =
      templateConfigs[session.printFlag] || templateConfigs.basic
    const cashlessOptionCount = templateConfig.options.filter((o) =>
      isCashlessSupported(o)
    ).length
    funnel.send('dialog_open', {
      format_id: session.formatid,
      print_flag: session.printFlag,
      default_print_type: session.printType,
      default_is_cashless: isCashlessSupported({ id: session.printType }),
      cashless_option_count: cashlessOptionCount,
      total_option_count: templateConfig.options.length,
      ui_version: 'dialog_v2_collapse',
      funnel_session_id: session.funnelSessionId
    })
    session.viewedSections = {}
  }

  function closeDialog() {
    if (session.funnelSessionId) {
      const timeSpent = session.dialogOpenedAt
        ? Math.round((Date.now() - session.dialogOpenedAt.getTime()) / 1000)
        : 0
      funnel.send('dialog_close', {
        format_id: session.formatid,
        is_completed: session.eprintResultCode !== '100',
        last_print_type: session.printType,
        time_spent_seconds: timeSpent,
        funnel_session_id: session.funnelSessionId
      })
    }
    session.dialogLayoutReady = false
    deps.stopPaypayRecheck()
    session.visible = false
    globalVar.dn_flag = 'eprint'
    emitter.emit('display_rt_popup')
    emitter.emit('eprint-dialog-closed')
    deps.cleanupMap()
  }

  function changePaymentMethod() {
    session.eprintResultCode = '100'
    session.isPrePayment = false
    session.selectedStore = null
    session.lastPaymentData = null
    deps.cleanupMap()
    if (cookies.get('paypay_payment_history_id')) {
      cookies.remove('paypay_payment_history_id')
    }
    scrollToTop()
  }

  function onDialogOpened() {
    if (session.eprintResultCode !== '100') {
      scrollToTop()
    }
    if (session.funnelSessionId) {
      session.dialogLayoutReady = true
    }
  }

  function dataChanged() {
    session.eprintResultCode = '100'
    session.eprintPdfEnabled = true
  }

  function onOpenPayload(payload: unknown) {
    const args = Array.isArray(payload) ? payload : []
    openDialog(
      args[0] as string,
      args[1] as OpenHtml,
      args[2] as string | undefined,
      args[3] as string | undefined,
      args[4] as boolean | undefined
    )
  }

  function restoreEprintDialogFromPaypay(eprintData: {
    printType?: string
    formatId?: string
    fontName?: string
    previewHtml?: string
  }) {
    session.printType = eprintData.printType || session.printType
    session.formatid = eprintData.formatId || session.formatid
    session.selFontStyle4DB = eprintData.fontName || ''
    session.previewHtml = eprintData.previewHtml || ''
    session.eprintResultCode = '100'
    session.isPaypayProcessing = true
    session.visible = true
  }

  function checkPaypayEprintReturn() {
    const urlParams = new URLSearchParams(window.location.search)
    const paypayReturn = urlParams.get('paypay_eprint_return')
    const merchantIdFromUrl = urlParams.get('merchant_payment_id')
    const merchantIdFromStorage = localStorage.getItem(
      'paypay_eprint_merchant_id'
    )
    if (paypayReturn !== '1') {
      if (merchantIdFromStorage) {
        localStorage.removeItem('paypay_eprint_merchant_id')
        localStorage.removeItem('paypay_eprint_data')
      }
      return
    }
    if (!merchantIdFromUrl && !merchantIdFromStorage) return
    const paymentHistoryId = cookies.get('paypay_payment_history_id')
    if (paymentHistoryId) {
      session.lastPaymentData = {
        paymentType: 'paypay',
        paymentHistoryId,
        timestamp: new Date().toISOString()
      }
    }
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.hash
    )
    const eprintData = localStorage.getItem('paypay_eprint_data')
    if (eprintData) {
      try {
        restoreEprintDialogFromPaypay(JSON.parse(eprintData))
      } catch (e) {
        console.error('PayPay Eprint データの復元に失敗:', e)
        session.visible = true
      }
    } else {
      session.isPaypayProcessing = true
      session.visible = true
    }
    deps.completePaypayEprint(merchantIdFromUrl || merchantIdFromStorage || '')
  }

  onMounted(() => {
    emitter.on('open-eprint-dialog', onOpenPayload)
    emitter.on('basic-data-changed', dataChanged)
    emitter.on('format-data-changed', dataChanged)
    checkPaypayEprintReturn()
  })
  onBeforeUnmount(() => {
    emitter.off('open-eprint-dialog', onOpenPayload)
    emitter.off('basic-data-changed', dataChanged)
    emitter.off('format-data-changed', dataChanged)
  })
  useEventListener(window, 'popstate', closeDialog)

  watch(
    () => session.eprintResultCode,
    (newVal, oldVal) => {
      if (newVal !== '100') {
        scrollToTop()
        if (newVal === '200') deps.searchNearCurrentLocation()
      } else if (oldVal !== '100') {
        session.isPrePayment = false
        session.selectedStore = null
      }
    }
  )
  watch(
    () => session.printType,
    (newVal, oldVal) => {
      if (oldVal && newVal !== oldVal && session.funnelSessionId) {
        funnel.send('print_type_select', {
          print_type: newVal,
          print_type_label: funnel.getPrintTypeLabel(newVal),
          price: funnel.getPrintTypePrice(newVal),
          format_id: session.formatid,
          is_cashless: isCashlessSupported({ id: newVal }),
          previous_print_type: oldVal,
          previous_is_cashless: isCashlessSupported({ id: oldVal }),
          funnel_session_id: session.funnelSessionId
        })
      }
    }
  )
  watch(
    () => session.selectedStore,
    (newVal, oldVal) => {
      if (newVal && newVal !== oldVal && session.funnelSessionId) {
        const isAutoSelected =
          session.isPrePayment && session.eprintResultCode === '200' && !oldVal
        funnel.send('store_select', {
          store_key: newVal,
          store_name: funnel.getStoreLabel(newVal),
          previous_store: oldVal || null,
          format_id: session.formatid,
          print_type: session.printType,
          is_prepayment: session.isPrePayment,
          auto_selected: isAutoSelected,
          funnel_session_id: session.funnelSessionId
        })
      }
    }
  )

  return {
    openDialog,
    closeDialog,
    changePaymentMethod,
    onDialogOpened,
    scrollToTop,
    dataChanged,
    isAuthenticated
  }
}
